import { message } from 'ant-design-vue'
import { saveChannelConfig } from '@/api/payManage/appManage'
import { OK, reportEvent } from '@/common'
import type { Ref } from 'vue'

interface UseSavePaySettingOptions {
  curPayChannel: Ref<string>
  detailForm: Ref<any>
  detailObj: Ref<any>
  props: any
  getChannelConfig: (type: string) => void
  refsMap: Record<string, Ref<any> | undefined>
}

/**
 * 保存支付配置 hooks
 * @param curPayChannel 当前支付渠道
 * @param detailForm 当前表单数据 ref
 * @param detailObj 当前详情数据 ref
 * @param props 组件 props
 * @param getChannelConfig 刷新渠道配置方法
 * @returns { submitForm, savePaySetting }
 */
export function useSavePaySetting({
  curPayChannel,
  detailForm,
  detailObj,
  props,
  getChannelConfig,
  refsMap,
}: UseSavePaySettingOptions) {
  // 保存表单
  const submitForm = async (formObj: any) => {
    Object.assign(detailForm.value, formObj)
    let params: { [paramsName: string]: any } = {
      accountId: detailObj.value?.accountId,
      mchId: detailObj.value?.mchId,
    }
    switch (curPayChannel.value) {
      case 'google':
      case 'apple':
      case 'amazon':
      case 'aptoide_pay':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: curPayChannel.value,
              pkg: detailForm.value.pkg,
              channelData: {
                divide: detailForm.value.divide,
              },
            },
          ],
        }
        break
      case 'vivo':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: 'vivo',
              pkg: detailForm.value.pkg,
              channelData: {
                appId: detailForm.value.appId,
                appKey: detailForm.value.appKey,
                divide: detailForm.value.divide,
              },
            },
          ],
        }
        break
      case 'oppo':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: 'oppo',
              pkg: detailForm.value.pkg,
              channelData: {
                appId: detailForm.value.appId,
                appKey: detailForm.value.appKey,
                divide: detailForm.value.divide,
                publicKey: detailForm.value.publicKey,
                privateKey: detailForm.value.privateKey,
                appSecret: detailForm.value.appSecret,
              },
            },
          ],
        }
        break
      case 'xiaomi':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: 'xiaomi',
              pkg: detailForm.value.pkg,
              channelData: {
                appId: detailForm.value.appId,
                appKey: detailForm.value.appKey,
                divide: detailForm.value.divide,
                appSecret: detailForm.value.appSecret,
              },
            },
          ],
        }
        break
      case 'ksminigame':
      case 'ksiosminigame':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: curPayChannel.value,
              pkg: detailForm.value.pkg,
              channelData: {
                appId: detailForm.value.appId,
                callbackSecret: detailForm.value.callbackSecret,
                paySecret: detailForm.value.paySecret,
                divide: detailForm.value.divide,
              },
            },
          ],
        }
        break
      case 'wxPayBaseConfig': {
        const wechatFormChannelDataList = detailForm.value.pkgList.map((item: any) => {
          return {
            id: item.id,
            dbtId: props.productItem.dbtId,
            channelType: 'wxpay',
            pkg: item.pkg,
            channelData: {
              appId: item.appId,
              appSecret: item.appSecret,
              mchId: detailObj.value?.mchId,
              channelRate: detailForm.value.rate,
            },
          }
        })
        params = {
          ...params,
          channelDataList: wechatFormChannelDataList,
        }
        break
      }
      case 'aliPayBaseConfig': {
        detailForm.value.pkgListTemp = JSON.stringify(detailForm.value.pkgList)
        const alipayChannelDataList = detailForm.value.pkgList.map((item: any) => {
          return {
            id: item.id,
            dbtId: props.productItem.dbtId,
            channelType: 'alipay',
            pkg: item.pkg,
            channelData: {
              appId: detailForm.value.appId,
              mchId: detailObj.value?.mchId,
              appPublicKey: detailForm.value.appPublicKey,
              appPrivateKey: detailForm.value.appPrivateKey,
              appGateway: detailForm.value.appGateway,
              channelRate: detailForm.value.channelRate,
            },
          }
        })
        params = {
          ...params,
          channelDataList: alipayChannelDataList,
        }
        break
      }
      case 'aliWebPayBaseConfig':
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                appId: detailForm.value.appId,
                mchId: detailObj.value?.mchId,
                appPrivateKey: detailForm.value.appPrivateKey,
                appGateway: detailForm.value.appGateway,
                channelRate: detailForm.value.channelRate,
              },
              channelType: 'alipay_web',
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
              pkg: detailForm.value.pkg,
            },
          ],
        }
        break
      case 'aliminigame':
      case 'aliminigame_ios':
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                appId: detailForm.value.appId,
                appPrivateKey: detailForm.value.appPrivateKey,
                appPublicKey: detailForm.value.appPublicKey,
                divide: detailForm.value.divide,
              },
              channelType: curPayChannel.value,
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
              pkg: detailForm.value.pkg,
            },
          ],
        }
        break
      case 'jsApiPayBaseConfig':
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                appId: detailForm.value.appId,
                mchId: detailObj.value?.mchId,
                divide: detailForm.value.divide.split('%')[0] / 100,
                wxPriKey: detailForm.value?.wxPriKey,
                certSerialNo: detailForm.value?.certSerialNo,
                apiKeyV3: detailForm.value?.apiKeyV3,
                channelRate: detailForm.value.rate,
              },
              channelType: 'jsapipay',
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
              pkg: detailForm.value.pkg,
            },
          ],
        }
        break
      case 'wxMinGamePayBaseConfig':
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                divide: detailForm.value.divide,
                appId: detailForm.value.appId,
                envConfig: detailForm.value.envConfig,
                zoneId: detailForm.value.zoneId,
                offerId: detailForm.value.offerId,
                appKey: detailForm.value.appKey,
                appSecret: detailForm.value.appSecret,
                mchId: detailObj.value?.mchId,
                wxPriKey: detailForm.value?.wxPriKey,
                apiKeyV3: detailForm.value?.apiKeyV3,
                rate: detailForm.value?.rate,
              },
              channelType: 'wxminigame',
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
              pkg: detailForm.value.pkg,
            },
          ],
        }
        break
      case 'wxMinGameIosPayBaseConfig':
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                divide: detailForm.value.divide,
                appId: detailForm.value.appId,
                envConfig: detailForm.value.envConfig,
                zoneId: detailForm.value.zoneId,
                offerId: detailForm.value.offerId,
                appKey: detailForm.value.appKey,
                appSecret: detailForm.value.appSecret,
                mchId: detailObj.value?.mchId,
                wxPriKey: detailForm.value?.wxPriKey,
                apiKeyV3: detailForm.value?.apiKeyV3,
                rate: detailForm.value?.rate,
              },
              channelType: 'wxminigame_ios',
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
              pkg: detailForm.value.pkg,
            },
          ],
        }
        break
      case 'douyinUnityPayBaseConfig':
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                mchId: detailObj.value?.mchId,
                appId: detailForm.value.appId,
                appSecret: detailForm.value.appSecret,
                signSecret: detailForm.value.signSecret,
                divide: detailForm.value.divide,
                rate: detailForm.value.rate,
              },
              channelType: 'douyingame',
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
              pkg: detailForm.value.pkg,
            },
          ],
        }
        break
      case 'douyinOrdinaryMiniGame':
        detailForm.value.signSecret = detailForm.value.signKey
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                mchId: detailObj.value?.mchId,
                appId: detailForm.value.appId,
                appSecret: detailForm.value.appSecret,
                signSecret: detailForm.value.signKey,
                rate: detailForm.value.rate,
              },
              channelType: 'douyinOrdinaryMiniGame',
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
              pkg: detailForm.value.pkg,
            },
          ],
        }
        break
      case 'wxnativepay':
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                mchId: detailObj.value?.mchId,
                appId: detailForm.value.appId,
                wxPriKey: detailForm.value?.wxPriKey,
                certSerialNo: detailForm.value?.certSerialNo,
                apiKeyV3: detailForm.value?.apiKeyV3,
                channelRate: detailForm.value.rate,
              },
              channelType: 'wxnativepay',
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
              pkg: detailForm.value.pkg,
            },
          ],
        }
        break
      case 'huawei':
      case 'harmony':
      case 'huawei_foreign':
      case 'huawei_mg':
      case 'huawei_runtime_fastgame':
        params = {
          ...params,
          channelDataList: [
            {
              channelData: {
                appId: detailForm.value.appId,
                divide: detailForm.value.divide,
                publishKey: detailForm.value.publishKey,
                // 华为快游戏额外需要游戏公私钥
                ...(curPayChannel.value === 'huawei_runtime_fastgame'
                  ? {
                      gamePublicKey: detailForm.value.gamePublicKey,
                      gamePrivateKey: detailForm.value.gamePrivateKey,
                    }
                  : {}),
              },
              pkg: detailForm.value.pkg,
              channelType: curPayChannel.value,
              dbtId: props.productItem.dbtId,
              id: detailForm.value?.id,
            },
          ],
        }
        break
      case 'tiktok':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: curPayChannel.value,
              pkg: detailForm.value.pkg,
              channelData: {
                appId: detailForm.value.appId,
                appSecret: detailForm.value.appSecret,
                divide: detailForm.value.divide,
              },
            },
          ],
        }
        break
      case 'bilibili_minigame_pay':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: 'bilibili_minigame_pay',
              pkg: detailForm.value.pkg,
              channelData: {
                appId: detailForm.value.appId,
                appKey: detailForm.value.appKey,
                divide: detailForm.value.divide,
                accessKey: detailForm.value.accessKey,
                accessToken: detailForm.value.accessToken,
                appSecret: detailForm.value.appSecret,
              },
            },
          ],
        }
        break
      case 'meta_vr_pay':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: 'meta_vr_pay',
              pkg: detailForm.value.pkg,
              channelData: {
                appId: detailForm.value.appId,
                appKey: detailForm.value.appKey,
                divide: detailForm.value.divide,
                accessKey: detailForm.value.accessKey,
                appSecret: detailForm.value.appSecret,
              },
            },
          ],
        }
        break
      case 'honor_minigame':
        params = {
          ...params,
          channelDataList: [
            {
              id: detailForm.value.id,
              dbtId: props.productItem.dbtId,
              channelType: 'honor_minigame',
              pkg: detailForm.value.pkg,
              channelData: {
                appId: detailForm.value.appId,
                appSecret: detailForm.value.appSecret,
                publishKey: detailForm.value.publishKey,
                clientId: detailForm.value.clientId,
                divide: detailForm.value.divide,
              },
            },
          ],
        }
        break
    }

    try {
      const { code, msg } = await saveChannelConfig(params)
      if (code === OK) {
        message.success('配置成功')
        getChannelConfig('update')
        reportEvent({
          eventName: 'element_use',
          properties: {
            ui_name: '产品管理',
            element_name: '支付配置',
          },
        })
      } else {
        message.error(msg)
      }
    } catch (e) {
      console.error(e)
    }
  }

  // 统一保存入口，自动校验
  const savePaySetting = () => {
    const ref = refsMap[curPayChannel.value]
    ref?.value?.validateForm && ref.value.validateForm()
  }

  return { submitForm, savePaySetting }
}
